import * as React from "react";
import { Button, message } from "antd";
import { stringSort, numberSort } from "utils/defaultSort";
import { FormatData } from "utils/FormatData";
import { dayjs } from "Config/dayjs";
import { ColumnProps } from "antd/lib/table";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ProjectFilesSearchForm } from "./ProjectFilesSearchForm";
import { UserName } from "Components/General/UserName";
import { Table } from "Components/General/Table";
import { formatUsername } from "utils/formatUsername";

interface ProjectFilesSearchProps extends WithTranslation {
  onExit: () => void;
  onCancel: () => void;
  addFiles: (fileIds: API_FileAttachment[]) => Promise<void>;
  data: API_SharedAttachments["items"];
  /* to filter only specified extensions */
  fileType?: string;
  /* to filter by a set of allowed extensions (takes precedence over fileType) */
  allowedFileTypes?: string[];
}

interface ProjectFilesSearchState {
  showSearchForm: boolean;
  searchResults: API_SearchResult[];
  selectedRowKeys: number[];
  loading: boolean;
}

class SearchResultsColumn extends React.Component<ColumnProps<API_SearchResult>> {}

export const ProjectFilesSearch = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<ProjectFilesSearchProps, ProjectFilesSearchState> {
      constructor(props: ProjectFilesSearchProps) {
        super(props);
        this.state = {
          showSearchForm: true,
          searchResults: [],
          selectedRowKeys: [],
          loading: false,
        };
      }

      onSelectChange = (selectedRowKeys: number[]) => {
        this.setState({ selectedRowKeys });
      };

      handleSearch = async (postObject: API_DocSearchReq) => {
        try {
          this.setState({ loading: true });
          // tslint:disable-next-line:max-line-length
          let response: API_SearchResult[] = await API.newData(
            API_URL.SEARCH.getSearchResults(projectsDataStore.projectId),
            postObject
          );
          const projectFileIds = this.props.data?.map((f) => f.id) || [];

          if (this.props.allowedFileTypes?.length) {
            response = response.filter((item) => this.props.allowedFileTypes.includes(item.type?.toLocaleLowerCase()));
          } else if (this.props.fileType) {
            response = response.filter((item) => item.type?.toLocaleLowerCase() === this.props.fileType);
          }

          response = response.filter((item) => {
            // filter already attached files
            return !projectFileIds.some((selected) => selected === item.id);
          });
          this.setState({ searchResults: response, showSearchForm: false });
        } catch (err) {
          throw err;
        } finally {
          this.setState({ loading: false });
        }
      };

      handleAddAttachments = async () => {
        const { selectedRowKeys, searchResults } = this.state;
        if (!selectedRowKeys.length) {
          message.error(this.props.t("No files were selected"));
        } else {
          const files: API_FileAttachment[] = [];
          selectedRowKeys.forEach((selectedId) => {
            const res = searchResults.find((item) => item.id === selectedId);
            files.push(res);
          });
          try {
            this.setState({ loading: true });
            await this.props.addFiles(files);
            this.props.onExit();
          } catch (error) {
            throw error;
          } finally {
            this.setState({ loading: false });
          }
        }
      };

      render() {
        const { t } = this.props;
        const { selectedRowKeys, searchResults, loading, showSearchForm } = this.state;
        if (showSearchForm) {
          return (
            <ProjectFilesSearchForm loading={loading} onCancel={this.props.onCancel} onSubmit={this.handleSearch} />
          );
        }
        return (
          <>
            <div className="mb-2">
              <Table
                name="docsFileSearch"
                dataSource={searchResults}
                rowKey="id"
                rowSelection={{
                  selectedRowKeys: selectedRowKeys,
                  onChange: this.onSelectChange,
                }}
                pagination={{ hideOnSinglePage: true }}
              >
                <SearchResultsColumn
                  title={t("Filename")}
                  dataIndex={"name"}
                  sorter={(a, b) => stringSort(a, b, "name")}
                />
                <SearchResultsColumn
                  title={t("Size")}
                  dataIndex={"size"}
                  render={(text) => FormatData.formatBytes(text, 1)}
                  sorter={(a, b) => numberSort(a.size, b.size)}
                />
                <SearchResultsColumn
                  title={t("Last modified")}
                  dataIndex={"created"}
                  render={(text) => dayjs(+text).format("lll")}
                  sorter={(a, b) => numberSort(a.created, b.created)}
                />
                <SearchResultsColumn
                  title={t("Owner")}
                  dataIndex={"ownerId"}
                  render={(text, record) => (
                    <UserName
                      id={text}
                      fallback={formatUsername({ firstName: record.ownerFirstName, surname: record.ownerSurname })}
                    />
                  )}
                />
              </Table>
            </div>
            <ButtonsRow justify="space-between">
              <Button onClick={this.props.onCancel} type="default">
                <FontAwesomeIcon
                  icon={["far", "chevron-left"]}
                  style={{ marginRight: 5 }}
                  fixedWidth={true}
                  size="lg"
                />
                {t("Back")}
              </Button>
              <Button onClick={this.handleAddAttachments} loading={loading} type="primary">
                {t("Add files")}
              </Button>
            </ButtonsRow>
          </>
        );
      }
    }
  )
);
